'use client';

import {
  eventFingerprint, levelAllowed, isClientLogLevel,
  MAX_MSG, MAX_STACK, MAX_EVENTS_POR_ENVIO,
  type ClientLogEvent, type ClientLogLevel,
} from '@/lib/client-log-events';

/**
 * Reporte de fallos del navegador.
 *
 * El orden importa: los escuchas se instalan de inmediato y lo que ocurra
 * mientras llega la configuración se guarda en memoria, porque el fallo que más
 * interesa —el que rompe la primera carga— pasa justo en esa ventana. Cuando
 * llega la respuesta, o se envía lo acumulado o se desinstala todo y se tira.
 *
 * Si la recolección está apagada, esto no deja nada instalado: ni escuchas, ni
 * temporizador, ni peticiones.
 */

const ENDPOINT = '/api/client-log';
const DEDUP_MS = 60_000;
const ESPERA_ENVIO_MS = 2000;
const MAX_PENDIENTES = 20;

let instalado = false;
let habilitado: boolean | null = null; // null = todavía no se sabe
let nivel: ClientLogLevel = 'error';
let pendientes: ClientLogEvent[] = [];
const vistos = new Map<string, number>();
let temporizador: ReturnType<typeof setTimeout> | null = null;

function recortar(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…(+${s.length - max})` : s;
}

function repetido(e: ClientLogEvent): boolean {
  const huella = eventFingerprint(e);
  const ahora = Date.now();
  const previo = vistos.get(huella);
  if (previo && ahora - previo < DEDUP_MS) return true;
  vistos.set(huella, ahora);
  if (vistos.size > 100) {
    for (const [k, t] of vistos) if (ahora - t > DEDUP_MS) vistos.delete(k);
  }
  return false;
}

function encolar(e: ClientLogEvent): void {
  if (habilitado === false) return;
  if (habilitado === true && !levelAllowed(e.level, nivel)) return;
  if (repetido(e)) return;
  // El tope protege el caso que motiva todo esto: un fallo dentro de un efecto
  // que se vuelve a montar sin parar. Se conservan los primeros, que son los que
  // explican el origen.
  if (pendientes.length >= MAX_PENDIENTES) return;
  pendientes.push(e);
  if (habilitado === true) programarEnvio();
}

function programarEnvio(): void {
  if (temporizador) return;
  temporizador = setTimeout(() => { temporizador = null; enviar(); }, ESPERA_ENVIO_MS);
}

function enviar(): void {
  if (habilitado !== true || pendientes.length === 0) return;
  const lote = pendientes.slice(0, MAX_EVENTS_POR_ENVIO);
  pendientes = pendientes.slice(lote.length);
  const cuerpo = JSON.stringify({ events: lote });
  // `keepalive` para que el envío sobreviva a la navegación que a veces sigue al
  // fallo. Un error aquí se traga a propósito: reportarlo dispararía otro envío.
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: cuerpo,
    keepalive: true,
  }).catch(() => { /* sin reintento: el fallo del reporte no puede reportarse */ });
}

/** Al cerrar la pestaña ya no hay tiempo para un fetch normal. */
function enviarAlCerrar(): void {
  if (habilitado !== true || pendientes.length === 0) return;
  const lote = pendientes.splice(0, MAX_EVENTS_POR_ENVIO);
  try {
    const blob = new Blob([JSON.stringify({ events: lote })], { type: 'application/json' });
    navigator.sendBeacon?.(ENDPOINT, blob);
  } catch { /* la pestaña se va: no hay nada más que hacer */ }
}

function deError(err: unknown): { msg: string; stack?: string } {
  if (err instanceof Error) {
    return { msg: recortar(`${err.name}: ${err.message}`, MAX_MSG), stack: err.stack ? recortar(err.stack, MAX_STACK) : undefined };
  }
  if (typeof err === 'string') return { msg: recortar(err, MAX_MSG) };
  try { return { msg: recortar(JSON.stringify(err) ?? 'error desconocido', MAX_MSG) }; }
  catch { return { msg: 'error no serializable' }; }
}

/** Reporte explícito desde el código de la página. */
export function reportClientError(
  err: unknown,
  opts: { level?: ClientLogLevel; kind?: string; srcReq?: string; digest?: string } = {},
): void {
  if (typeof window === 'undefined') return;
  encolar({
    level: opts.level ?? 'error',
    kind: opts.kind ?? 'manual',
    ...deError(err),
    path: window.location.pathname,
    srcReq: opts.srcReq,
    digest: opts.digest,
  });
}

function onError(ev: ErrorEvent): void {
  const { msg, stack } = deError(ev.error ?? ev.message);
  encolar({
    level: 'error', kind: 'error', msg, stack,
    path: window.location.pathname,
  });
}

function onRejection(ev: PromiseRejectionEvent): void {
  const { msg, stack } = deError(ev.reason);
  encolar({
    level: 'error', kind: 'unhandledrejection', msg, stack,
    path: window.location.pathname,
  });
}

function onOcultar(): void {
  if (document.visibilityState === 'hidden') enviarAlCerrar();
}

function desinstalar(): void {
  window.removeEventListener('error', onError);
  window.removeEventListener('unhandledrejection', onRejection);
  window.removeEventListener('pagehide', enviarAlCerrar);
  document.removeEventListener('visibilitychange', onOcultar);
  if (temporizador) { clearTimeout(temporizador); temporizador = null; }
  pendientes = [];
  vistos.clear();
}

export function installClientLogging(): () => void {
  if (typeof window === 'undefined' || instalado) return () => {};
  instalado = true;

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('pagehide', enviarAlCerrar);
  document.addEventListener('visibilitychange', onOcultar);

  fetch(ENDPOINT, { cache: 'no-store' })
    .then(res => (res.ok ? res.json() : null))
    .then((cfg: { enabled?: boolean; level?: string } | null) => {
      if (!cfg?.enabled) {
        habilitado = false;
        desinstalar();
        instalado = false;
        return;
      }
      habilitado = true;
      if (isClientLogLevel(cfg.level)) nivel = cfg.level;
      pendientes = pendientes.filter(e => levelAllowed(e.level, nivel));
      if (pendientes.length) programarEnvio();
    })
    .catch(() => {
      // Sin respuesta —sesión vencida, servidor caído— se asume apagada: sin
      // confirmación no se manda nada.
      habilitado = false;
      desinstalar();
      instalado = false;
    });

  return () => { desinstalar(); instalado = false; };
}
